import { Danger } from "iconsax-reactjs"
import { ProcessorKey } from "../../../shared/assets/svg"
import { BodyText, Button, HeadingText } from "../../../shared/ui"
import { Link } from "react-router-dom"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../../shared/ui/input-otp"
import { useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { verifyMfaService } from "../service"
import { VerifyMfaDataType } from "../types"
import { toast } from "sonner"
import { PAGES } from "../../../shared/lib/pageUrlConstants"
import { useDispatch } from "react-redux"
import { cn } from "../../../shared/lib"
import { NAV_LINKS } from "../../../features/navigation"
import { login } from "../../../redux/slices/authSlice"
import Loader from '../../../../legacy/components/loader'
import { useGeolocation } from "../../../features/account-settings/hooks/use-geolocation"

const VerificationCode = () => {
  const [value, setValue] = useState("")
  const [errorMsg, setErrorMsg] = useState("")
  const { userLocation, locationStatus } = useGeolocation();

  const { mutateAsync, isError, isPending } = useMutation({ mutationFn: (data: VerifyMfaDataType) => verifyMfaService(data) })
  const dispatch = useDispatch()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (locationStatus === 'denied') {
      toast.error("Please allow location permission to continue")
      return;
    }

    if ('Notification' in window) {
      if (Notification.permission === 'denied') {
        toast.error("Please allow notification permission to continue")
        return
      }

      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') {
          toast.error("Please allow notification permission to continue")
          return
        }
      }
    }

    const data: VerifyMfaDataType = {
      code: value,
      latitude: userLocation?.latitude,
      longitude: userLocation?.longitude
    }

    try {
      const res = await mutateAsync(data)
      toast.success(res?.message)
      localStorage.setItem("token", res?.data?.token);
      dispatch(login({ admin: res?.data?.admin, token: res?.data?.token }));
      if (res?.data?.admin) {
        const page = res?.data?.admin?.defaultModuleOnLogin
        const findPage = (Object.values(NAV_LINKS)).flat()?.find(module => module.text === page)
        if (findPage) {
          window.location.replace(findPage.link)
        } else {
          window.location.replace(PAGES.dashboard);
        }
      } else {
        window.location.replace(PAGES.dashboard);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Something went wrong"
      setErrorMsg(message)

      const isSessionError = message.toLowerCase().includes("expired") || message.toLowerCase().includes("session")

      if (isSessionError) {
        toast.error(`${message}. Redirecting to login page in 3 seconds...`, {
          position: "top-right",
          duration: 3000,
        })
        setTimeout(() => {
          window.location.replace(PAGES.login)
        }, 3000)
      } else {
        toast.error(message)
      }
    }
  }

  if (locationStatus === 'requesting') return <div className='flex flex-col items-center justify-center h-screen gap-3 p-4'>
    <Loader />
    <p className="text-center font-medium text-lg">Getting your location...</p>
    <p className="text-sm text-gray-50 text-center max-w-sm">
      Please allow location access when prompted by your browser to continue.
    </p>
  </div>


  return (
    <div className="bg-white max-w-[350px] md:max-w-[628px] mx-auto w-11/12  rounded-[32px] p-4 md:p-[32px] shadow-[0px_4px_40px_rgba(0,0,0,0.02)] border border-primary-80">
      <div className="flex flex-col items-start gap-6">
        <div className='p-0 relative h-12'>
          <ProcessorKey className="absolute -top-5 -left-6" />
        </div>

        <div className="space-y-1">
          <HeadingText className="text-2xl md:text-3xl font-semibold leading-tight">
            Confirm your sign-in
          </HeadingText>
          <BodyText className="text-gray-80 text-sm font-[350]">
            Please enter the 6-digit code from your authenticator app
          </BodyText>
        </div>

        <form className="w-full space-y-4 mt-2" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <InputOTP maxLength={6} value={value} onChange={(value) => setValue(value)} autoFocus>
              <InputOTPGroup className="flex gap-2">
                <InputOTPSlot index={0} className={cn((value.length === 6 && isError) && "bg-error-20")} />
                <InputOTPSlot index={1} className={cn((value.length === 6 && isError) && "bg-error-20")} />
                <InputOTPSlot index={2} className={cn((value.length === 6 && isError) && "bg-error-20")} />
                <InputOTPSlot index={3} className={cn((value.length === 6 && isError) && "bg-error-20")} />
                <InputOTPSlot index={4} className={cn((value.length === 6 && isError) && "bg-error-20")} />
                <InputOTPSlot index={5} className={cn((value.length === 6 && isError) && "bg-error-20")} />
              </InputOTPGroup>
            </InputOTP>
            {(isError && errorMsg) && <p className="text-error-30 flex items-center gap-1 font-[350] text-xs"><Danger size={20} /> <span>{errorMsg}</span></p>}
          </div>

          <div className="flex items-center justify-between gap-5 pt-2">
            <Link
              to="/recover-code"
              className="text-sm font-[450] hover:text-gray-30 text-primary-20 transition-colors"
            >
              Can’t access device
            </Link>

            <div className="flex items-center justify-between gap-5">
              <a
                href="mailto:admin@tradevu.co"
                className="text-sm font-[450] text-gray-30 hover:text-primary-20 transition-colors"
                target="_blank"
                rel="noreferrer"
              >
                Having trouble?
              </a>
              <Button
                type="submit"
                size="lg"
                disabled={isPending || !value || value.length !== 6}
                className="!h-[40px] rounded-[8px] text-base font-[450] bg-[#161827] hover:bg-[#20243a]"
              >
                {isPending ? "Loading..." : "Verify"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default VerificationCode
